import Controller from './controller'
import { elementId } from '../utilities'

export default class extends Controller {
  static get targets() {
    return ['inner', 'content']
  }

  static get options() {
    return {
      speed: 50,
      reverse: false,
    }
  }

  initialize() {
    this.resize = this.resize.bind(this)
    this.resizeDebounced = this.resizeDebounced.bind(this)
    this.checkPageVisibility = this.checkPageVisibility.bind(this)

    this.resize()
    this.createAnimation()
    this.checkPageVisibility()
    this.playWhenInView()

    this.element.classList.add('is-shown')
  }

  connect() {
    this.resizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        if (entry.contentRect.height) {
          this.resizeDebounced()
        }
      })
    })

    this.resizeObserver.observe(this.element)

    document.addEventListener('visibilitychange', this.checkPageVisibility)
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.resizeObserver) this.resizeObserver.disconnect()
    this.pause()
    document.removeEventListener('visibilitychange', this.checkPageVisibility)
  }

  setPlayState() {
    if (this.isPageVisible && this.isInView && !this.isHovered) {
      this.play()
    } else {
      this.pause()
    }
  }

  play() {
    if (this.isPlaying) return
    this.isPlaying = true

    this.element.classList.add('is-playing')
    this.innerTarget.style.animationPlayState = 'running'
  }

  pause() {
    if (!this.isPlaying) return
    this.isPlaying = false

    this.element.classList.remove('is-playing')
    this.innerTarget.style.animationPlayState = 'paused'
  }

  playWhenInView() {
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        this.isInView = entry.isIntersecting
        this.setPlayState()
      })
    })

    this.observer.observe(this.element)
  }

  createAnimation() {
    const animationName = elementId('_animation_')
    const style = document.createElement('style')

    style.textContent = `
      @keyframes ${animationName} {
        to {
          transform: translateX(calc(var(--marquee-width) * -1));
        }
      }
    `

    document.head.appendChild(style)
    this.innerTarget.style.animationName = animationName

    if (this.options.reverse) {
      this.innerTarget.style.animationDirection = 'reverse'
    }
  }

  resize() {
    const { width } = this.contentTarget.getBoundingClientRect()
    if (width === 0) return

    const count = Math.ceil(window.innerWidth / width)

    this.innerTarget.style.animationDuration = `${width / this.options.speed}s`
    this.element.style.setProperty('--marquee-width', `${width}px`)

    if (count !== this.prevCount) {
      if (this.extraContent) {
        this.extraContent.forEach(content => {
          this.innerTarget.removeChild(content)
        })
      }

      this.extraContent = Array.from(Array(count)).map(() => {
        const content = this.contentTarget.cloneNode(true)
        content.removeAttribute('data-target')

        // Remove nested data-targets inside the cloned node
        Array.from(content.querySelectorAll('[data-target]')).forEach(el => {
          el.removeAttribute('data-target')
        })

        // Also remove visible animation classes/targets
        Array.from(content.querySelectorAll('[data-visible-target="item"]')).forEach(element => {
          element.removeAttribute('data-visible-target')
          element.classList.remove('animate-up', 'animate-fade', 'animate-down')
        })

        this.innerTarget.appendChild(content)
        return content
      })

      this.prevCount = count
    }
  }

  resizeDebounced() {
    clearTimeout(this.resizeDebouncedTimeout)
    this.resizeDebouncedTimeout = setTimeout(this.resize, 150)
  }

  checkPageVisibility() {
    this.isPageVisible = document.visibilityState !== 'hidden'
    this.setPlayState()
  }

  hover() {
    this.isHovered = true
    this.setPlayState()
  }

  unhover() {
    this.isHovered = false
    this.setPlayState()
  }
}
